/**
 * Enhanced due diligence on a high-risk case.
 *
 * The checklist and the API to record it shipped together; the screen did not.
 * So when EDD gated an approval, the only way to clear it was someone writing
 * rows in the database — and the gate exists precisely so that a human looks at
 * a high-risk client and decides, with their name on the decision.
 *
 * Each item is either provided (with the document it is evidenced by) or waived
 * (with the reason it was waived). A waiver is not a shortcut: it is a recorded,
 * audited judgement, which is why it asks for the same note.
 */
import { useCallback, useEffect, useState } from 'react';
import StatusBadge from '../StatusBadge';
import workflowService, {
  type EDDChecklist,
  type EDDEvidenceType,
} from '../../services/workflow.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { colors } from '../../styles/theme';

const LABEL: Record<EDDEvidenceType, string> = {
  source_of_funds: 'Where the money comes from',
  tax_certification: 'Tax certification',
  foreign_id_screening: 'Foreign identity document checked',
};

const EXPLAIN: Record<EDDEvidenceType, string> = {
  source_of_funds:
    'Evidence of how the client came by the funds — payslips, a sale contract, audited accounts.',
  tax_certification:
    'The client’s tax residence and identification, evidenced rather than stated.',
  foreign_id_screening:
    'A document we cannot validate against a Mexican register was checked another way, and how.',
};

const inputStyle = { border: `1px solid ${colors.border}` };

export default function EddEvidencePanel({ caseId, canRecord }: Readonly<{
  caseId: string;
  /** Recording is manager / super_admin; an agent sees the state. */
  canRecord: boolean;
}>): JSX.Element | null {
  const [checklist, setChecklist] = useState<EDDChecklist | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const reload = useCallback(async () => {
    try {
      setChecklist(await workflowService.eddEvidence(caseId));
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not read the due-diligence checklist.'));
    } finally {
      setLoaded(true);
    }
  }, [caseId]);

  useEffect(() => { void reload(); }, [reload]);

  // A case that requires nothing says nothing: most cases are not high risk,
  // and an empty panel on every one of them is noise that hides the real ones.
  if (!loaded || (checklist !== null && !checklist.edd_required)) return null;

  return (
    <section className="rounded-2xl p-5 mb-4"
             style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
      <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
        <h2 className="text-sm font-semibold" style={{ color: colors.muted }}>
          ENHANCED DUE DILIGENCE
        </h2>
        {checklist && (
          <StatusBadge tone={checklist.outstanding.length === 0 ? 'success' : 'warning'}>
            {checklist.outstanding.length === 0
              ? 'Complete'
              : `${checklist.outstanding.length} outstanding`}
          </StatusBadge>
        )}
      </div>
      <p className="text-sm mb-4" style={{ color: colors.muted }}>
        This client’s risk band requires more than the standard file. The case cannot be
        approved until each item below is provided or waived.
      </p>

      {error && (
        <div className="mb-3 p-3 rounded-lg text-sm"
             style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}>
          {error}
        </div>
      )}

      {checklist && (
        <ul className="space-y-3">
          {checklist.required.map((type) => (
            <EvidenceRow
              key={type}
              caseId={caseId}
              type={type}
              current={checklist.items.find((i) => i.evidence_type === type) ?? null}
              canRecord={canRecord}
              onDone={() => { void reload(); }}
              onError={setError}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function EvidenceRow({ caseId, type, current, canRecord, onDone, onError }: Readonly<{
  caseId: string;
  type: EDDEvidenceType;
  current: EDDChecklist['items'][number] | null;
  canRecord: boolean;
  onDone: () => void;
  onError: (message: string) => void;
}>): JSX.Element {
  const [open, setOpen] = useState<'provided' | 'waived' | null>(null);
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const settled = current?.status === 'provided' || current?.status === 'waived';
  // Both paths ask for a note: a waiver IS the decision, and "provided" without
  // saying which document proves it is not evidence, it is an assertion.
  const ready = note.trim().length >= 4 && !busy
    && (open === 'waived' || reference.trim().length >= 3);

  const record = async (): Promise<void> => {
    if (open === null) return;
    setBusy(true);
    try {
      await workflowService.recordEddEvidence(caseId, {
        evidence_type: type,
        status: open,
        document_reference: open === 'provided' ? reference.trim() : null,
        note: note.trim(),
      });
      setOpen(null);
      setReference('');
      setNote('');
      onDone();
    } catch (e: unknown) {
      onError(getApiErrorMessage(e, 'Could not record the item.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="rounded-xl p-4" style={{ border: `1px solid ${colors.border}` }}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-medium" style={{ color: colors.ink }}>{LABEL[type]}</div>
          <p className="text-xs mt-0.5" style={{ color: colors.muted }}>{EXPLAIN[type]}</p>
          {settled && current?.note && (
            <p className="text-xs mt-2" style={{ color: colors.inkSoft }}>{current.note}</p>
          )}
          {settled && current?.document_reference && (
            <p className="text-xs mt-1 font-mono" style={{ color: colors.mutedSoft }}>
              {current.document_reference}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <StatusBadge tone={settled ? 'success' : 'warning'}>
            {current?.status === 'provided' && 'Provided'}
            {current?.status === 'waived' && 'Waived'}
            {!settled && 'Outstanding'}
          </StatusBadge>
        </div>
      </div>

      {canRecord && !settled && open === null && (
        <div className="flex gap-2 mt-3">
          <button type="button" onClick={() => setOpen('provided')}
                  className="text-xs px-3 py-1.5 rounded-md font-semibold"
                  style={{ background: colors.navy, color: '#FFFFFF' }}>
            Record as provided
          </button>
          <button type="button" onClick={() => setOpen('waived')}
                  className="text-xs px-3 py-1.5 rounded-md font-semibold"
                  style={{ border: `1px solid ${colors.border}`, color: colors.ink }}>
            Waive it
          </button>
        </div>
      )}

      {!canRecord && !settled && (
        <p className="text-xs mt-2" style={{ color: colors.muted }}>A manager records this.</p>
      )}

      {open !== null && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: colors.border }}>
          {open === 'provided' && (
            <>
              <label htmlFor={`ref-${type}`} className="block text-xs font-semibold mb-1"
                     style={{ color: colors.inkSoft }}>
                Which document proves it?
              </label>
              <input
                id={`ref-${type}`}
                value={reference}
                onChange={(e) => setReference(e.target.value.slice(0, 200))}
                placeholder="Case document 3 — notarised sale contract, 12 Aug 2026"
                disabled={busy}
                className="w-full px-3 py-2 rounded-lg text-sm mb-3"
                style={inputStyle}
              />
            </>
          )}
          <label htmlFor={`note-${type}`} className="block text-xs font-semibold mb-1"
                 style={{ color: colors.inkSoft }}>
            {open === 'waived' ? 'Why is it safe to waive this?' : 'What did you check?'}
          </label>
          <textarea
            id={`note-${type}`}
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 2000))}
            disabled={busy}
            className="w-full px-3 py-2 rounded-lg text-sm mb-3"
            style={inputStyle}
          />
          <div className="flex gap-2">
            <button type="button" disabled={!ready} onClick={() => { void record(); }}
                    className="px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50"
                    style={{ background: colors.navy, color: '#FFFFFF' }}>
              {busy ? 'Recording…' : 'Record'}
            </button>
            <button type="button" onClick={() => setOpen(null)} disabled={busy}
                    className="px-4 py-2 rounded-lg text-sm font-semibold"
                    style={{ border: `1px solid ${colors.border}`, color: colors.ink }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </li>
  );
}
